import React from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { supportContact } from "./constant";

/**
 * Seo component
 * Props:
 * - title: page title (appended to site name if needed)
 * - description: meta description
 * - canonical: absolute canonical URL (including https://domain)
 * - image: absolute URL for social preview image
 * - meta: array of additional meta objects ({ name|property, content })
 */
export default function Seo({ title, description, canonical, image, meta = [] }) {
  const siteName = "ZyNerd Portal";
  const defaultDescription =
    description || "ZyNerd Portal - Your NEET counseling partner with accurate college prediction.";

  // Compute canonical from location when not provided (works in browser)
  let computedCanonical = canonical;
  try {
    const location = useLocation();
    if (!computedCanonical && typeof window !== "undefined") {
      // Get canonical domain from environment variable
      const canonicalDomain = process.env.REACT_APP_CANONICAL_URL || "https://zynerd.com";
      computedCanonical = `${canonicalDomain}${location.pathname}`;
    }
  } catch (e) {
    // useLocation may throw during non-router contexts; fall back to canonical prop
  }

  // Add noindex for non-production environments unless explicitly overridden in meta
  const isProduction = process.env.REACT_APP_ENV === "production";
  const hasRobotsMeta = meta.some((m) => m.name === "robots");
  const metaWithRobots =
    !isProduction && !hasRobotsMeta
      ? [...meta, { name: "robots", content: "noindex, nofollow" }]
      : meta;

  return (
    <Helmet>
      {title ? <title>{`${title} | ${siteName}`}</title> : <title>{siteName}</title>}
      <meta name="description" content={defaultDescription} />

      {computedCanonical && <link rel="canonical" href={computedCanonical} />}

      {/* Open Graph */}
      <meta property="og:site_name" content={siteName} />
      {title && <meta property="og:title" content={title} />}
      <meta property="og:description" content={defaultDescription} />
      {image && <meta property="og:image" content={image} />}
      {computedCanonical && <meta property="og:url" content={computedCanonical} />}

      {/* Twitter */}
      <meta name="twitter:card" content={image ? "summary_large_image" : "summary"} />
      {title && <meta name="twitter:title" content={title} />}
      <meta name="twitter:description" content={defaultDescription} />
      {image && <meta name="twitter:image" content={image} />}

      {/* Contact fallback for metadata consumers */}
      <meta name="author" content={supportContact.EMAIL} />

      {/* Extra custom meta tags */}
      {metaWithRobots.map((m, i) => {
        const key = m.name || m.property || `meta-${i}`;
        if (m.property) return <meta key={key} property={m.property} content={m.content} />;
        return <meta key={key} name={m.name} content={m.content} />;
      })}
    </Helmet>
  );
}
